import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    // Honeypot fields - these should remain empty
    website: '', // This is the honeypot field
    company: ''  // Another honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  
  // Track when form was loaded for timing-based bot detection
  const formLoadTime = useRef(Date.now());

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    setMessageType('');

    // Check if form was submitted too quickly (likely a bot)
    const submitTime = Date.now();
    const timeTaken = (submitTime - formLoadTime.current) / 1000; // in seconds
    
    if (timeTaken < 3) {
      setSubmitMessage('Please take your time to fill out the form properly.');
      setMessageType('error');
      setIsSubmitting(false);
      return;
    }

    // Check honeypot fields (if filled, it's likely a bot)
    if (formData.website || formData.company) {
      // Silently fail for bots - show success but don't actually send
      setSubmitMessage('Thank you for your message. We will get back to you soon!');
      setMessageType('success');
      setFormData({ name: '', email: '', message: '', website: '', company: '' });
      setIsSubmitting(false);
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');
      
      // Include submission metadata for server-side validation
      const submissionData = {
        name: formData.name,
        email: formData.email,
        message: formData.message,
        // Include honeypot fields for server validation
        _honeypot: {
          website: formData.website,
          company: formData.company,
          timeTaken: timeTaken
        }
      };
      
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || 'An error occurred');
        }

        setSubmitMessage('Thank you for your message. We will get back to you soon!');
        setMessageType('success');
        setFormData({ name: '', email: '', message: '', website: '', company: '' });
        // Reset form load time
        formLoadTime.current = Date.now();
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const textData = await response.text();
        console.error('Received non-JSON response:', textData);
        throw new Error('Received an invalid response from the server');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitMessage(error.message || 'An error occurred. Please try again later.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="name" className="text-gray-700 font-medium">
          Your Name *
        </Label>
        <Input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="John Doe"
          className="h-12 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email" className="text-gray-700 font-medium">
          Email Address *
        </Label>
        <Input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          placeholder="john@example.com"
          className="h-12 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
        />
      </div>
      
      {/* Honeypot field 1 - Hidden from users */}
      <div style={{ position: 'absolute', left: '-9999px', height: '0', width: '0', overflow: 'hidden' }}>
        <Label htmlFor="website">
          Website (Leave this field empty)
        </Label>
        <Input
          type="text"
          id="website"
          name="website"
          value={formData.website}
          onChange={handleChange}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>
      
      {/* Honeypot field 2 - Hidden from users */}
      <div style={{ position: 'absolute', left: '-9999px', height: '0', width: '0', overflow: 'hidden' }}>
        <Label htmlFor="company">
          Company (Leave this field empty)
        </Label>
        <Input
          type="text"
          id="company"
          name="company"
          value={formData.company}
          onChange={handleChange}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="message" className="text-gray-700 font-medium">
          Your Message *
        </Label>
        <Textarea
          id="message"
          name="message"
          rows="5"
          value={formData.message}
          onChange={handleChange}
          required
          placeholder="Tell us how we can help you..."
          className="border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 resize-none"
        />
      </div>
      
      <Button 
        type="submit" 
        disabled={isSubmitting}
        className="w-full h-12 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold shadow-md hover:shadow-lg transition-all duration-300"
      >
        {isSubmitting ? 'Sending...' : 'Send Message'}
      </Button>
      
      {submitMessage && (
        <Alert className={messageType === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
          <AlertDescription className={messageType === 'success' ? 'text-green-800' : 'text-red-800'}>
            {submitMessage}
          </AlertDescription>
        </Alert>
      )}
    </form>
  );
};

const MapComponent = () => {
  // Coordinates for 7025 Beracasa Way, Boca Raton, FL 33433
  const position = [26.3689, -80.1685];

  return (
    <MapContainer center={position} zoom={16} style={{ height: '400px', width: '100%' }} className="rounded-xl shadow-lg">
      {/* Using CartoDB Voyager for more up-to-date and cleaner map data */}
      <TileLayer
        url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        subdomains='abcd'
        maxZoom={20}
      />
      
      {/* Alternative tile providers - uncomment one to use:
      
      // Option 2: CartoDB Positron (lighter, minimal style)
      <TileLayer
        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
      />
      
      // Option 3: Stadia Maps (requires free API key for production)
      <TileLayer
        url="https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png"
        attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
      />
      
      // Option 4: Esri World Street Map (very current business data)
      <TileLayer
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
        attribution='Tiles &copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom, 2012'
      />
      */}
      
      <Marker position={position}>
        <Popup>
          <div className="text-center">
            <strong>Oceanique Medical Spa</strong><br />
            7025 Beracasa Way, 103C<br />
            Boca Raton, FL 33433<br />
            <a href="https://www.google.com/maps/dir/?api=1&destination=7025+Beracasa+Way+103C+Boca+Raton+FL+33433" 
               target="_blank" 
               rel="noopener noreferrer"
               className="text-blue-600 hover:underline mt-2 inline-block"
            >
              Get Directions
            </a>
          </div>
        </Popup>
      </Marker>
    </MapContainer>
  );
};

const Contact = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700">
        {/* Background pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)',
          }}></div>
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="relative container mx-auto px-4 py-24">
          <div className="flex flex-col items-center justify-center text-center space-y-6">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white font-serif">
              Contact Us
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl">
              Contact us to schedule a consultation or learn more about our services
            </p>
          </div>
        </div>
        
        {/* Wave separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-16" viewBox="0 0 1440 100" fill="#f0f9ff">
            <path d="M0,50 C360,100 720,0 1440,50 L1440,100 L0,100 Z"></path>
          </svg>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 -mt-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Contact Form */}
          <Card className="shadow-xl border-0">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
              <CardTitle className="text-2xl font-serif">
                Send Us a Message
              </CardTitle>
              <p className="text-gray-600 mt-2">We'll respond within 24 hours</p>
            </CardHeader>
            <CardContent className="p-6">
              <ContactForm />
            </CardContent>
          </Card>
          
          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="shadow-lg border-0">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
                <CardTitle className="text-2xl font-serif">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Visit Us</h3>
                  <p className="text-gray-600">
                    7025 Beracasa Way, 103C<br />
                    Boca Raton, FL 33433
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Call Us</h3>
                  <a href="tel:5619906745" className="text-blue-600 hover:underline">
                    (561) 990-6745
                  </a>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Email Us</h3>
                  <a href="mailto:info@oceaniquebodycare.com" className="text-blue-600 hover:underline">
                    info@oceaniquebodycare.com
                  </a>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">Office Hours</h3>
                  <p className="text-gray-600">
                    Monday - Friday: 9am - 5pm<br />
                    <span className="text-sm text-blue-600 font-medium">By Appointment Only</span>
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Quick Info Card */}
            <Card className="bg-gradient-to-br from-blue-600 to-cyan-600 text-white border-0 shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-4">Schedule Your Visit</h3>
                <p className="text-blue-100 mb-4">
                  Schedule a consultation to discuss your treatment options.
                </p>
                <a 
                  href="/booking" 
                  className="inline-block bg-white text-blue-600 px-6 py-3 rounded-full font-semibold hover:bg-blue-50 transition-all duration-300"
                >
                  Book Appointment
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Map Section */}
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold font-serif mb-2">
              <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                Find Us
              </span>
            </h2>
            <p className="text-gray-600">Located in Boca Raton, Florida</p>
          </div>
          <Card className="overflow-hidden shadow-xl border-0">
            <MapComponent />
          </Card>
        </div>
      </div>


    </div>
  );
};

export default Contact;